import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { ReactComponent as LocationImage } from "../../icons/locationIcon.svg";

import { ReactComponent as KitchenIcon } from '../../icons/KitchenIcon.svg'
import { ReactComponent as CredentialIcon } from '../../icons/credentialIcon.svg'
import { ReactComponent as OrderIcon } from '../../icons/OrderIcon.svg'
import { ReactComponent as DocumentationIcon } from '../../icons/documentationIcon.svg'
import { ReactComponent as FundingIcon } from '../../icons/fundingIcon.svg'
import { ReactComponent as ExpertIcon } from '../../icons/expertIcon.svg'
import { ReactComponent as HeartIcon } from "../../icons/HeartIcon.svg";
import { ReactComponent as LocationIcon } from "../../icons/locationsavedIcon.svg";
import { ReactComponent as StarIcon } from "../../icons/StarIcon.svg";
import { ReactComponent as PlanIcon } from "../../icons/saleIcon.svg";
import { ReactComponent as CommunityIcon } from "../../icons/communityIcon.svg";

import { SavedResourceCardProps } from "../../data/types";
import { capitalizeString, getDirectLinkFromGoogleDriveSharedLink } from '../../utils/helpers';

const SavedResourceCard: React.FC<SavedResourceCardProps> = ({
  resource,
  handleRemoveSavedResource,
  isMobile
}) => {

  const navigate = useNavigate();


  var ResourceIcon = OrderIcon
  const resourceType = resource?.resource_type ? resource.resource_type : ""

  switch(resourceType){
    case 'kitchen':
      ResourceIcon = KitchenIcon
      break;

    case 'documentation':
      ResourceIcon = DocumentationIcon
      break;

    case 'training':
      ResourceIcon = CredentialIcon
      break;

    case 'funding':
      ResourceIcon = FundingIcon
      break;

    case 'expert':
      ResourceIcon = ExpertIcon
      break;

    case 'farm':
      ResourceIcon = OrderIcon
      break;

    default:
      ResourceIcon = OrderIcon
  }


  const getBgColor = () => {
    return "#fff";
  };

  const getPrimaryTextColor = () => {
    return "#202124";
  };

  const getSecondaryTextColor = () => {
    return "#8A8A8A";
  };

  const bgPhotoUrls = useMemo(() => {
    const photos: string[] = [];
    
    const getPhotoUrl = (photo: string | undefined): string | null => {
      if (!photo) return null;
      
      if (photo.startsWith('http://') || photo.startsWith('https://')) {
        const convertedUrl = getDirectLinkFromGoogleDriveSharedLink(photo);
        return convertedUrl || photo;
      }
      
      const convertedUrl = getDirectLinkFromGoogleDriveSharedLink(photo);
      return convertedUrl || null;
    };
    
    if (resource.bgPhotos && resource.bgPhotos.length > 0) {
      resource.bgPhotos.forEach(photo => {
        const photoUrl = getPhotoUrl(photo);
        if (photoUrl && !photos.includes(photoUrl)) {
          photos.push(photoUrl);
        }
      });
    }
    
    if (resource.bgPhoto) {
      const bgPhotoUrl = getPhotoUrl(resource.bgPhoto);
      if (bgPhotoUrl && !photos.includes(bgPhotoUrl)) {
        photos.push(bgPhotoUrl);
      }
    }
    
    return photos;
  }, [resource.bgPhotos, resource.bgPhoto]);
  
  const photosKey = useMemo(() => {
    return resource.bgPhotos?.join(',') || '';
  }, [resource.bgPhotos]);
  
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [imageError, setImageError] = useState<boolean[]>([]);
  // const [imageLoaded, setImageLoaded] = useState<boolean[]>([]);
  
  useEffect(() => {
    setCurrentPhotoIndex(0);
    const length = bgPhotoUrls.length || 0;
    setImageError(new Array(length).fill(false));
    // setImageLoaded(new Array(length).fill(false));
  }, [bgPhotoUrls.length, photosKey, resource.bgPhoto]);
  
  const safePhotoIndex = Math.min(currentPhotoIndex, Math.max(0, bgPhotoUrls.length - 1));
  // const currentPhotoUrl = bgPhotoUrls.length > 0 ? bgPhotoUrls[safePhotoIndex] : null;
  
  const handleImageLoad = (index: number) => {
    // setImageLoaded(prev => {
    //   const newState = [...prev];
    //   if (newState.length <= index) {
    //     newState.length = index + 1;
    //   }
    //   newState[index] = true;
    //   return newState;
    // });
    setImageError(prev => {
      const newState = [...prev];
      if (newState.length <= index) {
        newState.length = index + 1;
      }
      newState[index] = false;
      return newState;
    });
  };
  
  const handleImageError = (index: number) => {
    setImageError(prev => {
      const newState = [...prev];
      if (newState.length <= index) {
        newState.length = index + 1;
      }
      newState[index] = true;
      return newState;
    });
  };

  const logoUrl = resource.logo ? getDirectLinkFromGoogleDriveSharedLink(resource.logo) : null;
  const planCount = resource.plans?.length || 0;
  // Access community data (attached by useLoadSavedResources hook)
  const community = (resource as any).community;
  const communityName = community?.name;
  // console.log("resource", resource)
  const handleCardClick = async () => {
    if (resourceType && resource?.resource_id){
      navigate(`/${resourceType}/${resource.resource_id}`)
    }
  }

  // const openInNewTab = (url: string) => {
  //   if (url){
  //     window.open(url, "_blank");
  //   }
  // };

  const handlePreviousPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (bgPhotoUrls.length > 1) {
      setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : bgPhotoUrls.length - 1));
    }
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (bgPhotoUrls.length > 1) {
      setCurrentPhotoIndex((prev) => (prev < bgPhotoUrls.length - 1 ? prev + 1 : 0));
    }
  };
  
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  const minSwipeDistance = 50;
  
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && bgPhotoUrls.length > 1) {
      setCurrentPhotoIndex((prev) => (prev < bgPhotoUrls.length - 1 ? prev + 1 : 0));
    }
    if (isRightSwipe && bgPhotoUrls.length > 1) {
      setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : bgPhotoUrls.length - 1));
    }
  };

  if (isMobile){
    return (
        <div 
          className={`flex flex-col rounded-xl border-[1px] border-[#E9E9E9] w-full shadow-sm overflow-hidden cursor-pointer bg-white mt-5 p-2`} 
          onClick={handleCardClick}
        >
          <div 
            className="relative w-full h-64 bg-gray-200 overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {bgPhotoUrls.length > 0 ? (
              bgPhotoUrls.map((photoUrl, index) => (
                <div
                  key={`${photoUrl}-${index}`}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === safePhotoIndex ? 'opacity-100 z-0' : 'opacity-0 z-[-1] pointer-events-none'
                  }`}
                >
                  {imageError[index] !== true ? (
                    <img
                      src={photoUrl}
                      alt={resource.name ? `${resource.name}, view ${index + 1}` : `Resource view ${index + 1}`}
                      className="w-full h-full object-cover rounded-2xl"
                      onError={() => handleImageError(index)}
                      onLoad={() => handleImageLoad(index)}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-300 via-gray-200 to-gray-400 flex items-center justify-center">
                      <div className="text-center p-4">
                        <ResourceIcon className="w-16 h-16 text-gray-400 mx-auto mb-2 opacity-60" />
                        <p className="text-xs text-gray-500 font-raleway font-medium">
                          {resource.name || 'Resource'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-300 via-gray-200 to-gray-400 flex items-center justify-center">
                <div className="text-center p-4">
                  <ResourceIcon className="w-16 h-16 text-gray-400 mx-auto mb-2 opacity-60" />
                  <p className="text-xs text-gray-500 font-raleway font-medium">
                    {resource.name || 'Resource'}
                  </p>
                </div>
              </div>
            )}
            
            {resource.active && (
              <div className="absolute top-3 left-3">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#5DB6CE] text-white">
                  Featured
                </span>
              </div>
            )}

            <div
              className="absolute top-3 right-3 z-10"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveSavedResource(resource.id);
              }}
            >
              <div className="bg-black/30 rounded-full p-1.5 backdrop-blur-sm">
                <HeartIcon className="w-5 h-5 text-white" />
              </div>
            </div>

            {bgPhotoUrls.length > 1 && (
              <>
                <button
                  onClick={handlePreviousPhoto}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 rounded-full p-1.5 text-white transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft style={{ fontSize: 24 }} />
                </button>
                <button
                  onClick={handleNextPhoto}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 rounded-full p-1.5 text-white transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight style={{ fontSize: 24 }} />
                </button>
              </>
            )}

            {bgPhotoUrls.length > 1 && (
              <div 
                className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1.5 z-10"
                onClick={(e) => e.stopPropagation()}
              >
                {bgPhotoUrls.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentPhotoIndex(index);
                    }}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === safePhotoIndex ? 'bg-white w-6' : 'bg-white/50 w-2'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="p-4 bg-white">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg border border-[#E9E9E9] flex items-center justify-center bg-white flex-shrink-0">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={resource.name}
                    className="w-10 h-10 object-contain"
                  />
                ) : (
                  <ResourceIcon className="w-6 h-6 text-[#5DB6CE]/60" />
                )}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-raleway font-bold text-lg text-[#1A1A1A] truncate flex-1 min-w-0 text-[18px]">
                    {resource.name || 'Resource'}
                  </h3>
                  {resource.rating ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <StarIcon className="w-4 h-4 text-[#ED8A19]" />
                      <span className="text-sm font-raleway font-semibold text-[#1A1A1A] whitespace-nowrap">
                        {resource.rating.toFixed(1)}
                      </span>
                    </div>
                  ) : null}
                </div>
                {resource.description && (
                  <p className="text-sm text-[#808080] font-open-sans line-clamp-1 mt-1 text-[16px]">
                    {resource.description}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              {(resource.address || resource.city || resource.state) && (
                <div className="flex items-center gap-2 text-sm">
                  <LocationIcon className="w-6 h-6 text-[#5DB6CE] flex-shrink-0" />
                  <span className="text-[#4D4D4D] font-raleway font-medium truncate min-w-0 text-[16px]">
                    {resource.address ? `${resource.address}, ` : ''}
                    {resource.city ? `${resource.city}, ` : ''}
                    {resource.state ? `${resource.state}` : ''}
                    {!resource.address && !resource.city && !resource.state ? 'Nationwide' : ''}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-4 flex-wrap">
                {planCount > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <PlanIcon className="w-6 h-6 text-[#5DB6CE] flex-shrink-0" />
                    <span className="text-[#4D4D4D] font-open-sans text-[16px]">
                      {planCount} Booking Plans
                    </span>
                  </div>
                )}
                
                {communityName && (
                  <div className="flex items-center gap-2 text-sm min-w-0 flex-1">
                    <CommunityIcon className="w-6 h-6 text-[#5DB6CE] flex-shrink-0" />
                    <span className="text-[#4D4D4D] font-open-sans truncate text-[16px]">
                      {communityName}
                    </span>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
        )
  } 
  else {
    return (
      <div
        className={`flex flex-col ${resource.active ? 'h-[140px] ' : 'h-[120px] sm:h-[112px]'} sm:w-[1152px]  object-cover rounded-xl border-[1px] border-[#E9E9E9] mx-auto text-start p-4 cursor-pointer bg-gradient-to-a hover:bg-gradient-to-b from-[#f9f9f9] mt-5 `}
        style={{ backgroundColor: getBgColor(), opacity: 0.9 }}
      >
      
          <div className='flex flex-row '>
            {
              resource.active && (
                <div className='flex my-2 w-[86px] rounded-2xl bg-[#E7F5EC] mt-auto'>
                  <p className='m-auto text-[#0F973D] text-[14px] font-raleway font-normal'>Available</p>
                </div>
              )
            }
          </div>

          <div className='flex flex-row my-auto'>
            <div className='p-2 border-[#E9E9E9] w-16 border-[1px] rounded-md'>
              {
                getDirectLinkFromGoogleDriveSharedLink(resource?.logo) ? (
                  <img className=' h-10 aspect-auto object-contain' src={getDirectLinkFromGoogleDriveSharedLink(resource.logo)} alt={resource?.id} /> 
                  ) : (
                    <ResourceIcon className={'relative m-1 w-10 aspect-square p-1 text-[#5DB6CE]/60 '} />
                    )
              }
            </div>

            <div className="flex flex-col w-2/3 m-2">
              <p 
                className="font-open-sans font-bold px-2 text-sm sm:text-md"
                style={{ color: getPrimaryTextColor() }}
              >
                {resource.name}
              </p>
              <p
                className="px-2 font-open-sans text-xs sm:text-sm flex flex-row pt-1"
                style={{ color: getSecondaryTextColor(), opacity: 0.8 }}
              >
                <LocationImage className="mr-1 mt-[1px] w-[16px] h-[16px] text-gray" />
                {resource && resource.address ? `${resource.address}, ` : ``}
                {resource && resource.city ? `${resource.city}, ` : ``}
                {resource && resource.state ? `${resource.state} ` : ``}
                {resource && resource.zipcode ? `${resource.zipcode} ` : ``}

                {!resource ||
                (resource && !(
                  resource.address ||
                  resource.city ||
                  resource.state ||
                  resource.location ||
                  resource.zipcode
                ))
                  ? `Nationwide`
                  : ``}
              </p>
          
            </div>
      
            <div className="flex flex-row gap-2 ml-auto">
              <div className='flex flex-row text-primaryDark-400 px-3 cursor-pointer' onClick={() => handleRemoveSavedResource(resource.id)}>
                <HeartIcon className='text-primaryDark-400 w-[16px] h-[15px] my-auto' />
                <p className='mx-2 my-auto'>Saved</p>
              </div>
              <button onClick={handleCardClick} className="text-primaryDark-0 font-bold border-[1px] border-grayDark-100 w-[148px] text-[14px]">
                View {capitalizeString(resourceType)}
              </button>
            </div>
          </div>
      </div>
    );
  }
};

export default SavedResourceCard;
